import { FC } from 'react';

import {
  Col,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Switch,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { ScenarioForm } from '@/types/scenario';
import { normalizePositiveNumericValuesInput } from '@/utils';

type Props = {
  disabled?: boolean;
  form: FormInstance<ScenarioForm>;
};

const BatteryOptions: FC<Props> = ({ form, disabled = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const batteryDepletedOptions = [
    {
      label: t('scenario.form.batteryOptions.depletedOptions.replaceBatteries'),
      value: 'replace_batteries',
    },
    {
      label: t('scenario.form.batteryOptions.depletedOptions.stopSimulation'),
      value: 'stop_simulation',
    },
    {
      label: t(
        'scenario.form.batteryOptions.depletedOptions.continueWithoutStorage'
      ),
      value: 'continue_wo_storage',
    },
  ];

  return (
    <Form.Item
      style={{
        border: '0.5px solid',
        borderRadius: '10px',
        borderColor: theme.colors.grayLightAccent,
      }}
      className="p-16 pb-40"
      wrapperCol={{ span: 24 }}
    >
      <Form.Item<ScenarioForm>
        layout="horizontal"
        name="unitDegradation"
        label={t('scenario.form.batteryOptions.ratioShadowCost.label')}
        //TODO Reusable FormTooltips
        tooltip={{
          title: (
            <div>
              <>
                <Typography.Text className="fs-14-semi-bold">
                  {t(
                    'scenario.form.batteryOptions.ratioShadowCost.tooltip.title'
                  )}
                </Typography.Text>
                <div className="horizontal-line mt-4"></div>
              </>
              <Typography.Text className="fs-14-regular smoke mt-4">
                {t(
                  'scenario.form.batteryOptions.ratioShadowCost.tooltip.description'
                )}
              </Typography.Text>
            </div>
          ),
          styles: {
            body: {
              padding: theme.spacing.spacing12 + 'px',
              color: 'inherit',
            },
          },
          color: theme.colors.light,
        }}
      >
        <Switch
          disabled={disabled}
          checkedChildren={t('scenario.form.batteryOptions.switch.on')}
          unCheckedChildren={t('scenario.form.batteryOptions.switch.off')}
        />
      </Form.Item>
      <Row className="pt-24">
        <Col className="pr-8" span={12}>
          <Form.Item<ScenarioForm>
            name="whenBatteryIsDepleted"
            label={t(
              'scenario.form.batteryOptions.whenBatteryIsDepleted.label'
            )}
            rules={[
              {
                required: true,
                message: t(
                  'scenario.form.batteryOptions.whenBatteryIsDepleted.requiredMessage'
                ),
              },
            ]}
          >
            <Select
              disabled={disabled}
              placeholder={t(
                'scenario.form.batteryOptions.whenBatteryIsDepleted.placeholder'
              )}
              options={batteryDepletedOptions}
            />
          </Form.Item>
        </Col>
        <Col className="pl-8" span={12}>
          <Form.Item<ScenarioForm>
            shouldUpdate={(prevValues, currentValues) => {
              if (
                prevValues.whenBatteryIsDepleted === 'replace_batteries' &&
                currentValues.whenBatteryIsDepleted !== 'replace_batteries'
              ) {
                form.setFieldValue('replacementNumberLimit', undefined);
              }

              return currentValues.whenBatteryIsDepleted !== undefined;
            }}
          >
            {({ getFieldValue }) => {
              const isVisible =
                getFieldValue('whenBatteryIsDepleted') === 'replace_batteries';
              return isVisible ? (
                <Form.Item<ScenarioForm>
                  normalize={normalizePositiveNumericValuesInput}
                  name="replacementNumberLimit"
                  label={t(
                    'scenario.form.batteryOptions.replacementNumberLimit.label'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenario.form.batteryOptions.replacementNumberLimit.requiredMessage'
                      ),
                    },
                  ]}
                >
                  <Input
                    disabled={disabled}
                    placeholder={t(
                      'scenario.form.batteryOptions.replacementNumberLimit.placeholder'
                    )}
                  />
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
        </Col>
      </Row>
    </Form.Item>
  );
};

export default BatteryOptions;
